import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toLocalYMD } from '../lib/dates';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  X, Phone, Mail, MapPin, Calendar, Clock, 
  MessageSquare, Bell, CreditCard, FileText,
  ChevronRight, ChevronLeft, Send, ExternalLink, Facebook,
  AlertTriangle, Check, CheckCheck, PoundSterling, Receipt,
  Home, User, TrendingUp, History, Edit3,
  Camera, Navigation, Plus, MoreVertical, RefreshCw, Trash2, Banknote, Loader2
} from 'lucide-react';
import { zoneColors } from '../data/csvData';
import NewCustomerForm from './NewCustomerForm';
import CustomerNotesEditor from './CustomerNotesEditor';
import TakePaymentModal from './TakePaymentModal';
import SMSComposeModal from './SMSComposeModal';
import WhatsAppComposeModal from './WhatsAppComposeModal';
import { safeOpenUrl, openGoogleMapsSearch, openWazeNavigation } from '../utils/navigation';
import { customerChanged, jobChanged, Actions } from '../utils/dataSync';
import { toast } from 'sonner';

// Mock message history for customers
const generateMockMessages = (customerName, jobService) => {
  const firstName = customerName.split(' ')[0];
  const workerName = Math.random() > 0.5 ? 'Dan' : 'Steve';
  
  return [
    {
      id: 1,
      type: 'sms',
      direction: 'outbound',
      sender: 'Perfect Panes',
      recipient: customerName,
      content: `Hi ${firstName}, ${workerName} will be with you Wed, Feb 4th for the ${jobService}. Reply STOP to opt out.`,
      timestamp: '2026-02-03T09:30:00',
      status: 'delivered',
      automated: true,
      template: 'Job Reminder'
    },
    {
      id: 2,
      type: 'email',
      direction: 'outbound',
      sender: 'hello@perfectpanes.co.uk',
      recipient: customerName,
      subject: `Reminder: ${jobService} tomorrow`,
      content: `Hi ${firstName},\n\nJust a friendly reminder that ${workerName} will be visiting tomorrow for your ${jobService}.\n\nIf you need to reschedule, please let us know.\n\nBest regards,\nPerfect Panes`,
      timestamp: '2026-02-03T09:30:00',
      status: 'delivered',
      automated: true,
      template: 'Appointment Reminder'
    },
    {
      id: 3,
      type: 'sms',
      direction: 'inbound',
      sender: customerName,
      content: 'Thanks, see you then!',
      timestamp: '2026-02-03T10:15:00',
    },
    {
      id: 4,
      type: 'email',
      direction: 'outbound',
      sender: 'hello@perfectpanes.co.uk',
      recipient: customerName,
      subject: 'Invoice INV-2026-0042 from Perfect Panes',
      content: `Hi ${firstName},\n\nPlease find your invoice attached.\n\nAmount: £35.00\nDue: 17th February 2026\n\nPay online: https://perfectpanes.co.uk/pay/INV-2026-0042\n\nThank you for your business!\n\nPerfect Panes`,
      timestamp: '2026-01-20T14:00:00',
      status: 'opened',
      automated: true,
      template: 'Invoice Notification'
    },
    {
      id: 5,
      type: 'sms',
      direction: 'outbound',
      sender: 'Perfect Panes',
      recipient: customerName,
      content: `Hi ${firstName}, your ${jobService} is complete! Invoice to follow. Thanks - Perfect Panes`,
      timestamp: '2026-01-06T16:45:00',
      status: 'delivered',
      automated: true,
      template: 'Job Complete'
    },
  ];
};

// Mock alerts for customers
const generateMockAlerts = (customerName) => {
  return [
    {
      id: 1,
      type: 'reminder',
      title: 'Job reminder sent',
      description: 'Automated reminder sent via SMS and Email',
      timestamp: '2026-02-03T09:30:00',
      status: 'completed'
    },
    {
      id: 2,
      type: 'payment',
      title: 'Payment received',
      description: '£35.00 received via Stripe',
      timestamp: '2026-01-21T11:20:00',
      status: 'completed'
    },
    {
      id: 3,
      type: 'job',
      title: 'Job completed',
      description: 'Window Clean marked as done by Dan',
      timestamp: '2026-01-06T16:45:00',
      status: 'completed'
    },
  ];
};

const CustomerDetailModal = ({ customer, job, onClose, replannedDate, onDeleteCustomer }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customer');
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('all');
  const [scheduleIndex, setScheduleIndex] = useState(0); // For scrolling through dates
  const [showNavModal, setShowNavModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  // Minimum swipe distance to trigger close (in pixels)
  const minSwipeDistance = 100;
  
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    
    // Calculate swipe offset (only allow right swipe)
    if (touchStart !== null) {
      const offset = currentTouch - touchStart;
      if (offset > 0) {
        setSwipeOffset(offset);
      }
    }
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwipeOffset(0);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isRightSwipe = distance > minSwipeDistance;
    
    if (isRightSwipe) {
      onClose();
    }
    
    setSwipeOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  
  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [cancellingInvoice, setCancellingInvoice] = useState(null);
  const [localBalance, setLocalBalance] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Take Payment Modal state
  const [showTakePayment, setShowTakePayment] = useState(false);
  
  // SMS state
  const [showSMSCompose, setShowSMSCompose] = useState(false);
  const [showWaCompose, setShowWaCompose] = useState(false);

  const [smsMessages, setSmsMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Multiple jobs state
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJobIndex, setSelectedJobIndex] = useState(0);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch all jobs for this customer
  React.useEffect(() => {
    const fetchCustomerJobs = async () => {
      const custId = customer?.id || job?.customer_id || job?.customerRef;
      if (!custId) return;
      
      setLoadingJobs(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/jobs?customer_id=${custId}&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const jobs = await response.json();
          if (jobs.length > 0) {
            setCustomerJobs(jobs);
          } else if (job) {
            setCustomerJobs([job]);
          }
        }
      } catch (error) {
        console.error('Error fetching customer jobs:', error);
        if (job) setCustomerJobs([job]);
      }
      setLoadingJobs(false);
    };
    
    fetchCustomerJobs();
  }, [customer?.id, job?.customer_id, job?.customerRef, API_URL, job]);

  // Use hash for deterministic "random" values
  const hashCode = (str) => {
    if (!str) return 0;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  };

  const customerId = customer?.id || job?.customerRef || 'unknown';
  const hash = hashCode(customerId);

  // Get customer name from various sources - NEVER show UUID
  const getCustomerName = () => {
    // Helper to check if string looks like a UUID
    const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
    
    if (customer?.name && !isUUID(customer.name)) return customer.name;
    if (job?.customer_name && !isUUID(job.customer_name)) return job.customer_name;
    if (job?.customerName && !isUUID(job.customerName)) return job.customerName;
    return 'Unknown Customer';
  };

  const customerData = {
    id: customer?.id || job?.customer_id || job?.customerRef,
    name: getCustomerName(),
    email: customer?.email || job?.customer_email || job?.customerEmail || '',
    phone: customer?.phone || job?.customer_phone || job?.customerPhone || '',
    address: customer?.address || job?.address || 'Address not available',
    zone: customer?.zone || job?.zone || job?.round || '',
    balance: customer?.balance || 0,
    stripeActive: customer?.stripe_has_payment_method === true || customer?.has_stripe === true,
    goCardlessActive: customer?.has_gocardless === true || (!!(customer?.gocardless_mandate_id) && customer?.gocardless_mandate_status !== 'cancelled' && customer?.gocardless_mandate_status !== 'failed'),
    stripeCustomerId: customer?.stripe_customer_id || null,
    gocardlessCustomerId: customer?.gocardless_customer_id || null,
    gocardlessMandateId: customer?.gocardless_mandate_id || null,
    gocardlessMandateStatus: customer?.gocardless_mandate_status || null,
  };

  // Memoised WhatsApp recipient — prevents new object refs on every render.
  // NOTE: must be declared AFTER customerData (TDZ otherwise crashes the modal).
  const waRecipient = useMemo(() => ({
    id: customerData.id,
    customer_id: customerData.id,
    name: customerData.name,
    phone: customerData.phone,
    balance: customerData.balance,
  }), [customerData.id, customerData.name, customerData.phone, customerData.balance]);

  const jobData = job || {
    services: 'Window Clean',
    frequency: '6 Weeks',
    price: 35.00,
    lastDone: null,
    nextDue: null,
    assignee: 'Dan',
  };

  // Calculate frequency in weeks
  const getFrequencyWeeks = () => {
    const freq = (job?.frequency || jobData.frequency || '').toLowerCase();
    if (freq.includes('4 week') || freq.includes('4week')) return 4;
    if (freq.includes('8 week') || freq.includes('8week')) return 8;
    if (freq.includes('10 week') || freq.includes('10week')) return 10;
    if (freq.includes('12 week') || freq.includes('12week')) return 12;
    return 6; // default
  };
  
  const freqWeeks = getFrequencyWeeks();
  
  // Calculate displayed dates based on schedule index
  const getDisplayedDates = () => {
    const lastDoneStr = job?.lastDone || job?.last_done;
    const baseDate = lastDoneStr ? new Date(lastDoneStr) : new Date();
    
    // Last done shifts by scheduleIndex * frequency
    const lastDone = new Date(baseDate);
    lastDone.setDate(lastDone.getDate() + (scheduleIndex * freqWeeks * 7));
    
    // Next due is always frequency weeks after last done
    const nextDue = new Date(lastDone);
    nextDue.setDate(nextDue.getDate() + (freqWeeks * 7));
    
    return { lastDone, nextDue };
  };
  
  const { lastDone: displayedLastDone, nextDue: displayedNextDue } = getDisplayedDates();
  
  // Format date nicely
  const formatDateNice = (date) => {
    if (!date) return 'Not set';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' });
  };
  
  // Navigate to date in Work Planner
  const handleDateClick = (date) => {
    const dateStr = date instanceof Date ? toLocalYMD(date) : date;
    onClose();
    navigate(`/work-planner?date=${dateStr}`);
  };
  
  // Navigation handlers - using safe open to avoid popup blockers
  const handleOpenMaps = () => {
    openGoogleMapsSearch(customerData.address);
    setShowNavModal(false);
  };
  
  const handleOpenWaze = () => {
    openWazeNavigation(null, null, customerData.address);
    setShowNavModal(false);
  };

  // Fetch SMS + Email messages for customer (unified endpoint)
  const fetchSmsMessages = useCallback(async () => {
    const custId = customerData.id || job?.customer_id || job?.customerRef;
    if (!custId) return;
    
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${custId}/messages?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Endpoint returns {history: [{date, messages: [...]}], total_messages}
        // Flatten the grouped structure into a single list
        const flat = (data.history || []).flatMap(group => group.messages || []);
        setSmsMessages(flat);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
    setLoadingMessages(false);
  }, [customerData.id, job?.customer_id, job?.customerRef, API_URL]);

  // Fetch messages when Messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchSmsMessages();
    }
  }, [activeTab, fetchSmsMessages]);

  const alerts = useMemo(() => generateMockAlerts(customerData.name), [customerData.name]);

  // Fetch invoices when Finance tab is active
  React.useEffect(() => {
    if (activeTab === 'finance' && customerData.id) {
      fetchCustomerInvoices();
    }
  }, [activeTab, customerData.id]);

  // Initialize local balance
  React.useEffect(() => {
    setLocalBalance(customerData.balance);
  }, [customerData.balance]);

  const fetchCustomerInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/invoices?customer_id=${customerData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleCancelInvoice = async (invoiceId, invoiceTotal) => {
    if (!window.confirm('Are you sure you want to cancel this invoice? The amount will be removed from the customer balance.')) {
      return;
    }
    
    setCancellingInvoice(invoiceId);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        // Update local state
        setInvoices(prev => prev.map(inv => 
          inv.id === invoiceId ? { ...inv, status: 'cancelled' } : inv
        ));
        // Update balance
        if (localBalance !== null) {
          setLocalBalance(prev => Math.max(0, prev - invoiceTotal));
        }
      }
    } catch (err) {
      console.error('Error cancelling invoice:', err);
    } finally {
      setCancellingInvoice(null);
    }
  };

  if (!customer && !job) return null;

  const tabs = [
    { id: 'customer', label: 'Customer', icon: User },
    { id: 'finance', label: 'Finance', icon: CreditCard },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'alerts', label: 'Alerts', icon: Bell },
  ];

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    console.log(`Sending ${messageType} to ${customerData.name}: ${newMessage}`);
    setNewMessage('');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' }) + ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  };

  const renderCustomerTab = () => (
    <div className="space-y-4">
      {/* Customer Info Card */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-start gap-4">
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-md"
            style={{ backgroundColor: zoneColors[customerData.zone] || '#64748b' }}
          >
            {customerData.name.charAt(0)}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-zinc-900">{customerData.name}</h3>
            <p className="text-sm text-zinc-500 mt-1">{customerData.address}</p>
            {customerData.phone && (
              <a 
                href={`tel:${customerData.phone}`}
                className="text-sm text-emerald-600 font-medium mt-1 inline-flex items-center gap-1.5 hover:text-emerald-700"
              >
                <Phone size={14} />
                {customerData.phone}
              </a>
            )}
            <div className="flex items-center gap-2 mt-2">
              <span 
                className="text-xs font-medium px-2 py-1 rounded-md"
                style={{ 
                  backgroundColor: `${zoneColors[customerData.zone] || '#64748b'}15`,
                  color: zoneColors[customerData.zone] || '#64748b'
                }}
              >
                {customerData.zone}
              </span>
              {customerData.stripeActive && (
                <span className="text-xs font-medium px-2 py-1 rounded-md bg-purple-50 text-purple-600">Stripe</span>
              )}
              {customerData.goCardlessActive && (
                <span className="text-xs font-medium px-2 py-1 rounded-md bg-cyan-50 text-cyan-600">GoCardless</span>
              )}
            </div>
          </div>
        </div>

        {/* Contact Actions */}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-100">
          <a href={`tel:${customerData.phone}`} className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-colors">
            <Phone size={18} />
            <span className="font-medium text-sm">Call</span>
          </a>
          <a href={`mailto:${customerData.email}`} className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-50 hover:bg-blue-100 text-emerald-700 rounded-xl transition-colors">
            <Mail size={18} />
            <span className="font-medium text-sm">Email</span>
          </a>
          <button className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-xl transition-colors">
            <MessageSquare size={18} />
            <span className="font-medium text-sm">SMS</span>
          </button>
        </div>
      </div>

      {/* Customer Notes — same field as the main customer card. Edits here
          persist and appear everywhere customer.notes is shown. */}
      {customerData?.id && (
        <CustomerNotesEditor
          customerId={customerData.id}
          title="Notes"
          placeholder="Gate codes, dog warnings, where the tap is — anything the next visit should know"
        />
      )}

      {/* Service Info - All Jobs */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <Home size={16} className="text-zinc-400" />
          Jobs ({customerJobs.length || 1})
        </h4>
        <div className="space-y-3">
          {/* Show all jobs for this customer */}
          {(customerJobs.length > 0 ? customerJobs : [job]).filter(Boolean).map((jobItem, index) => (
            <div 
              key={jobItem?.id || index} 
              className={`p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                index === selectedJobIndex 
                  ? 'bg-emerald-50 border-emerald-300' 
                  : 'bg-zinc-50 border-transparent hover:border-zinc-200'
              }`}
              onClick={() => setSelectedJobIndex(index)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-zinc-900">
                    {jobItem?.services || jobItem?.service || 'Window Clean'}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {jobItem?.frequency || 'No frequency'}
                    {jobItem?.is_recurring === false && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Ad-hoc</span>
                    )}
                  </p>
                </div>
                <p className="text-lg font-bold text-zinc-900">£{(jobItem?.price || 0).toFixed(2)}</p>
              </div>
              {/* Show dates for selected job */}
              {index === selectedJobIndex && (
                <div className="mt-3 pt-3 border-t border-zinc-200 grid grid-cols-2 gap-2">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600 mb-1">Last Done</p>
                    <p className="text-sm font-medium text-zinc-900">
                      {jobItem?.last_done || jobItem?.lastDone 
                        ? formatDateNice(new Date(jobItem?.last_done || jobItem?.lastDone))
                        : 'Never'}
                    </p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600 mb-1">Next Due</p>
                    <p className="text-sm font-medium text-emerald-700">
                      {jobItem?.next_due || jobItem?.nextDue || jobItem?.scheduled_date
                        ? formatDateNice(new Date(jobItem?.next_due || jobItem?.nextDue || jobItem?.scheduled_date))
                        : 'Not scheduled'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {loadingJobs && (
            <div className="text-center py-4 text-zinc-500 text-sm">
              Loading jobs...
            </div>
          )}
          
          {/* Legacy navigation - hidden but keeping for compatibility */}
          <div className="hidden">
          {/* Last Done / Next Due with navigation arrows */}
          <div className="flex items-center">
            {/* Left Arrow */}
            <button 
              onClick={() => setScheduleIndex(scheduleIndex - 1)}
              disabled={scheduleIndex <= -5}
              className={`p-2 rounded-lg transition-colors ${scheduleIndex <= -5 ? 'text-zinc-200' : 'text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600'}`}
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex-1 grid grid-cols-2 gap-2">
              {/* Last Done - Clickable */}
              <button 
                onClick={() => handleDateClick(displayedLastDone)}
                className="p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left"
                title="Click to view in Work Planner"
              >
                <p className="text-xs text-purple-600 mb-1">Last Done</p>
                <p className="text-sm font-medium text-zinc-900">{formatDateNice(displayedLastDone)}</p>
              </button>
              
              {/* Next Due - Clickable */}
              <button 
                onClick={() => handleDateClick(displayedNextDue)}
                className="p-3 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors text-left"
                title="Click to view in Work Planner"
              >
                <p className="text-xs text-emerald-600 mb-1">Next Due</p>
                <p className="text-sm font-medium text-emerald-700">{formatDateNice(displayedNextDue)}</p>
              </button>
            </div>
            
            {/* Right Arrow */}
            <button 
              onClick={() => setScheduleIndex(scheduleIndex + 1)}
              disabled={scheduleIndex >= 5}
              className={`p-2 rounded-lg transition-colors ${scheduleIndex >= 5 ? 'text-zinc-200' : 'text-emerald-400 hover:bg-emerald-50 hover:text-emerald-600'}`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
          
          {scheduleIndex !== 0 && (
            <p className="text-xs text-zinc-400 text-center">
              {scheduleIndex < 0 ? `${Math.abs(scheduleIndex)} cycle${Math.abs(scheduleIndex) > 1 ? 's' : ''} ago` : `${scheduleIndex} cycle${scheduleIndex > 1 ? 's' : ''} ahead`}
            </p>
          )}
          </div>{/* End hidden legacy section */}
        </div>
      </div>

      {/* Map Preview - Clickable for navigation */}
      <div className="bg-white rounded-xl overflow-hidden shadow-sm">
        <div className="h-32 bg-zinc-200 flex items-center justify-center">
          <div className="text-center">
            <MapPin size={24} className="text-zinc-400 mx-auto mb-1" />
            <p className="text-xs text-zinc-500">Map Preview</p>
          </div>
        </div>
        <button 
          onClick={() => setShowNavModal(true)}
          className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 transition-colors"
        >
          <p className="text-sm text-zinc-600 truncate flex-1 text-left">{customerData.address}</p>
          <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
            <Navigation size={14} />
            Navigate
          </span>
        </button>
      </div>
      
      {/* Navigation Choice Modal */}
      {showNavModal && (
        <div 
          className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4"
          onClick={() => setShowNavModal(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-xs overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 text-center">Navigate to</h3>
              <p className="text-xs text-gray-500 text-center mt-1 line-clamp-2">{customerData.address}</p>
            </div>
            <div className="p-2">
              <button
                onClick={handleOpenMaps}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MapPin size={24} className="text-blue-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Google Maps</p>
                  <p className="text-xs text-gray-500">Open in Google Maps</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
              <button
                onClick={handleOpenWaze}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <Navigation size={24} className="text-cyan-600" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Waze</p>
                  <p className="text-xs text-gray-500">Open in Waze</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            </div>
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => setShowNavModal(false)}
                className="w-full p-3 text-gray-500 font-medium hover:bg-gray-50 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <button className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Camera size={18} className="text-emerald-600" />
          </div>
          <span className="text-sm font-medium text-zinc-700">Attachments</span>
        </button>
        <button className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all">
          <div className="p-2 bg-purple-50 rounded-lg">
            <ExternalLink size={18} className="text-purple-600" />
          </div>
          <span className="text-sm font-medium text-zinc-700">View Xero</span>
        </button>
      </div>
    </div>
  );

  const renderFinanceTab = () => (
    <div className="space-y-4">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-xl p-5 text-white">
        <p className="text-sm text-white/70 mb-1">Account Balance</p>
        <p className="text-3xl font-bold">£{(localBalance ?? customerData.balance)?.toFixed(2) || '0.00'}</p>
        {(localBalance ?? customerData.balance) > 0 && (
          <p className="text-amber-400 text-sm mt-2">Outstanding balance</p>
        )}
      </div>

      {/* Take Payment Button - Primary Action */}
      <button
        onClick={() => setShowTakePayment(true)}
        className="w-full flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl shadow-lg transition-all"
        data-testid="take-payment-btn"
      >
        <Banknote size={22} />
        <span className="text-lg font-semibold">Take Payment</span>
      </button>

      {/* Payment Method */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h4 className="font-semibold text-zinc-900 mb-3">Payment Method</h4>
        {customerData.stripeActive ? (
          <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CreditCard size={18} className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-800">Stripe Connected</p>
              <p className="text-xs text-emerald-600">Auto-charge enabled</p>
            </div>
            <Check size={18} className="text-emerald-600" />
          </div>
        ) : customerData.goCardlessActive ? (
          <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <CreditCard size={18} className="text-cyan-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-cyan-800">GoCardless Connected</p>
              <p className="text-xs text-cyan-600">Direct Debit active</p>
            </div>
            <Check size={18} className="text-cyan-600" />
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
            <div className="p-2 bg-zinc-100 rounded-lg">
              <CreditCard size={18} className="text-zinc-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-zinc-700">No payment method</p>
              <p className="text-xs text-zinc-500">Manual invoicing</p>
            </div>
            <button className="text-emerald-600 text-sm font-medium">Setup</button>
          </div>
        )}
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-zinc-900">Invoices</h4>
          <button 
            onClick={fetchCustomerInvoices}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
        
        {loadingInvoices ? (
          <div className="p-8 text-center text-zinc-400">
            Loading invoices...
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center text-zinc-400">
            No invoices found for this customer
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {invoices.map(inv => (
              <div 
                key={inv.id} 
                onClick={() => setSelectedInvoice(inv)}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity ${
                inv.status === 'cancelled' ? 'bg-red-50 border border-red-100' :
                inv.status === 'paid' ? 'bg-emerald-50 border border-emerald-100' :
                'bg-zinc-50'
              }`}>
                <div className="flex items-center gap-3">
                  <Receipt size={16} className={
                    inv.status === 'cancelled' ? 'text-red-400' :
                    inv.status === 'paid' ? 'text-emerald-500' :
                    'text-zinc-400'
                  } />
                  <div>
                    <p className={`text-sm font-medium ${inv.status === 'cancelled' ? 'text-red-700 line-through' : 'text-zinc-900'}`}>
                      {inv.invoice_number}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {new Date(inv.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    {inv.vat_amount > 0 && (
                      <p className="text-xs text-zinc-400">incl. VAT £{inv.vat_amount?.toFixed(2)}</p>
                    )}
                  </div>
                </div>
                <div className="text-right flex items-center gap-3">
                  <div>
                    <p className={`text-sm font-medium ${inv.status === 'cancelled' ? 'text-red-600 line-through' : 'text-zinc-900'}`}>
                      £{inv.total?.toFixed(2)}
                    </p>
                    <span className={`text-xs font-medium ${
                      inv.status === 'paid' ? 'text-emerald-600' :
                      inv.status === 'cancelled' ? 'text-red-500' :
                      inv.status === 'overdue' ? 'text-red-500' :
                      'text-amber-600'
                    }`}>
                      {inv.status}
                    </span>
                    {/* Delivery & Payment Status Badges */}
                    <div className="flex items-center gap-2 mt-1 justify-end">
                      {/* Stripe Success Badge — shown when invoice was auto-charged via Stripe */}
                      {inv.stripe_payment_intent_id && inv.status === 'paid' && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#635BFF]/10 text-[#635BFF]"
                          title={`Stripe payment succeeded — ${inv.stripe_payment_intent_id}`}
                          data-testid="stripe-success-badge"
                        >
                          <CreditCard size={11} />
                          <span className="text-[10px] font-bold uppercase tracking-wide">Stripe</span>
                          <Check size={11} strokeWidth={3} />
                        </span>
                      )}
                      {/* GoCardless badge — paid or pending */}
                      {(inv.gc_payment_id || inv.gocardless_payment_id) && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#1E3264]/10 text-[#1E3264]"
                          title={
                            inv.status === 'paid'
                              ? 'GoCardless payment confirmed'
                              : 'GoCardless payment in flight (3–5 working days)'
                          }
                          data-testid="gocardless-status-badge"
                        >
                          <CreditCard size={11} />
                          <span className="text-[10px] font-bold uppercase tracking-wide">GC</span>
                          {inv.status === 'paid' ? (
                            <Check size={11} strokeWidth={3} />
                          ) : (
                            <RefreshCw size={11} className="animate-spin-slow" />
                          )}
                        </span>
                      )}
                      {/* Email Badge - check both sent_at (from job completion) and resent_at (from manual resend) */}
                      {(inv.sent_at || inv.resent_at || inv.email_status) && (
                        <span className="flex items-center gap-0.5" title={
                          inv.email_status === 'opened' ? 'Email opened' :
                          inv.email_status === 'delivered' ? 'Email delivered' :
                          (inv.sent_at || inv.resent_at) ? 'Email sent' : ''
                        }>
                          <Mail size={12} className="text-blue-400" />
                          {inv.email_status === 'opened' ? (
                            <CheckCheck size={12} className="text-blue-500" />
                          ) : inv.email_status === 'delivered' || inv.sent_at || inv.resent_at ? (
                            <Check size={12} className="text-emerald-500" />
                          ) : null}
                        </span>
                      )}
                      {/* SMS Badge */}
                      {(inv.resent_sms_at || inv.sms_status) && (
                        <span className="flex items-center gap-0.5" title={
                          inv.sms_status === 'delivered' ? 'SMS delivered' :
                          inv.sms_status === 'sent' || inv.resent_sms_at ? 'SMS sent' : ''
                        }>
                          <MessageSquare size={12} className="text-emerald-400" />
                          {(inv.sms_status === 'delivered' || inv.sms_status === 'sent' || inv.resent_sms_at) && (
                            <Check size={12} className="text-emerald-500" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCancelInvoice(inv.id, inv.total); }}
                      disabled={cancellingInvoice === inv.id}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Cancel Invoice"
                    >
                      {cancellingInvoice === inv.id ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderMessagesTab = () => {
    // WhatsApp-style delivery ticks across all channels:
    //   • single grey tick     = Sent (server accepted)
    //   • double grey ticks    = Delivered to the handset / inbox
    //   • double blue ticks    = Read (only WhatsApp can confirm this; email
    //                             will only show double-blue when we have an
    //                             opened_at timestamp)
    //   • red triangle         = Failed
    //   • spinner              = Pending / queued
    const renderStatusIcon = (msg) => {
      const isWa = msg?.type === 'whatsapp';
      const isEmail = msg?.type === 'email';
      // Email "read" = `opened_at` set (Resend webhook fires `email.opened`)
      const isRead = (isWa && msg?.delivery_status === 'read') ||
                     (isEmail && msg?.opened_at);
      const status = msg?.delivery_status || msg?.status || '';

      if (status === 'failed') {
        return <AlertTriangle size={14} className="text-red-400" />;
      }
      if (status === 'pending' || status === 'queued') {
        return <Loader2 size={14} className="text-zinc-300 animate-spin" />;
      }
      if (isRead) {
        // Double blue tick (WhatsApp "read")
        return <CheckCheck size={14} className="text-[#34B7F1]" />;
      }
      if (status === 'delivered') {
        return <CheckCheck size={14} className="text-zinc-300" />;
      }
      // Default = single grey tick (Sent)
      return <Check size={14} className="text-zinc-300" />;
    };

    // Filter messages by selected type ('sms', 'email', 'whatsapp' or 'all')
    const displayMessages = smsMessages.filter(msg => 
      messageType === 'all' || msg.type === messageType
    ).sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));

    return (
      <div className="flex flex-col h-full">
        {/* Message Type Toggle — WhatsApp, SMS, Email, All. Four pills so
            users can quickly drill into one channel; All is the default. */}
        <div className="grid grid-cols-4 gap-2 p-3 bg-white rounded-xl shadow-sm mb-4">
          <button
            onClick={() => setMessageType('whatsapp')}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'whatsapp' ? 'bg-[#25d366] text-white' : 'bg-zinc-100 text-zinc-600'
            }`}
            data-testid="filter-whatsapp"
          >
            WhatsApp
          </button>
          <button
            onClick={() => setMessageType('sms')}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'sms' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'
            }`}
            data-testid="filter-sms"
          >
            SMS
          </button>
          <button
            onClick={() => setMessageType('email')}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'email' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'
            }`}
            data-testid="filter-email"
          >
            Email
          </button>
          <button
            onClick={() => setMessageType('all')}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              messageType === 'all' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'
            }`}
            data-testid="filter-all"
          >
            All
          </button>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto space-y-3 mb-4">
          {loadingMessages ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-zinc-400" size={24} />
            </div>
          ) : displayMessages.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare size={32} className="mx-auto text-zinc-300 mb-2" />
              <p className="text-sm text-zinc-500">No messages yet</p>
              <p className="text-xs text-zinc-400">Sent SMS and email invoices will appear here</p>
            </div>
          ) : (
            displayMessages.map(msg => {
              const isEmail = msg.type === 'email';
              const isWhatsapp = msg.type === 'whatsapp';
              // WhatsApp green / Email blue / SMS emerald. Pick palette once.
              const palette = isWhatsapp
                ? { bubble: 'bg-[#075e54] text-white', label: 'text-[#a7d8c5]', chip: 'bg-[#128c7e]/40 text-[#d2f5e3]', ts: 'text-xs text-[#a7d8c5]' }
                : isEmail
                  ? { bubble: 'bg-blue-600 text-white', label: 'text-blue-200', chip: 'text-xs px-1.5 py-0.5 rounded bg-blue-400/30 text-blue-100', ts: 'text-xs text-blue-200' }
                  : { bubble: 'bg-emerald-500 text-white', label: 'text-emerald-200', chip: 'text-xs px-1.5 py-0.5 rounded bg-emerald-400/30 text-emerald-100', ts: 'text-xs text-emerald-200' };
              const channelLabel = isWhatsapp ? 'WHATSAPP' : (isEmail ? 'EMAIL' : 'SMS');
              return (
                <div 
                  key={msg.id}
                  className="ml-4"
                  data-testid={`message-${msg.id}`}
                >
                  <div className={`rounded-xl p-3 ${palette.bubble}`}>
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare size={12} className={palette.label} />
                      <span className={`text-xs font-medium ${palette.label}`}>
                        {channelLabel} → {msg.recipient_name || msg.recipient || customerData.name?.split(' ')[0] || 'Customer'}
                      </span>
                      {msg.template_name && (
                        <span className={isWhatsapp ? 'text-xs px-1.5 py-0.5 rounded bg-[#128c7e]/40 text-[#d2f5e3]' : palette.chip}>
                          {msg.template_name}
                        </span>
                      )}
                    </div>

                    {/* Email subject (if present) */}
                    {isEmail && msg.subject && (
                      <p className="text-sm font-semibold text-white mb-1">
                        {msg.subject}
                      </p>
                    )}

                    {/* Content */}
                    <p className="text-sm whitespace-pre-wrap text-white/90 break-all" data-testid={`message-body-${msg.id}`}>
                      {(() => {
                        const text = msg.message || msg.content || '';
                        const urlRe = /(https?:\/\/[^\s]+)/g;
                        const parts = text.split(urlRe);
                        return parts.map((part, i) => {
                          if (urlRe.test(part)) {
                            // reset regex state
                            urlRe.lastIndex = 0;
                            return (
                              <a
                                key={i}
                                href={part}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-white font-medium"
                                data-testid={`message-link-${msg.id}`}
                              >
                                {part}
                              </a>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        });
                      })()}
                    </p>

                    {/* Footer with status — WhatsApp-style ticks across all
                        channels. Single grey = sent, double grey = delivered,
                        double BLUE = read (WhatsApp read receipt or email
                        `opened_at`). */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                      <span className={palette.ts}>
                        {formatTimestamp(msg.created_at || msg.timestamp)}
                      </span>
                      <div className="flex items-center gap-1">
                        {renderStatusIcon(msg)}
                        <span className={`text-xs ${
                          (isWhatsapp && msg.delivery_status === 'read') || (isEmail && msg.opened_at) ? 'text-[#34B7F1]' :
                          msg.delivery_status === 'delivered' ? 'text-white/90' : 
                          msg.delivery_status === 'failed' ? 'text-red-200' : 'text-white/60'
                        }`}>
                          {((isWhatsapp && msg.delivery_status === 'read') || (isEmail && msg.opened_at)) && 'Read'}
                          {msg.delivery_status === 'delivered' && !((isWhatsapp && msg.delivery_status === 'read') || (isEmail && msg.opened_at)) && 'Delivered'}
                          {msg.delivery_status === 'sent' && 'Sent'}
                          {msg.delivery_status === 'failed' && 'Failed'}
                          {(msg.delivery_status === 'pending' || msg.delivery_status === 'queued') && 'Sending...'}
                          {!msg.delivery_status && (msg.status || '')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Compose SMS / WhatsApp Buttons */}
        <div className="bg-white rounded-xl p-3 shadow-sm space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setShowSMSCompose(true)}
              className="flex items-center justify-center gap-2 px-3 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
              data-testid="compose-sms-btn"
            >
              <MessageSquare size={18} />
              SMS
            </button>
            <button
              onClick={() => setShowWaCompose(true)}
              disabled={!customerData.phone}
              className="flex items-center justify-center gap-2 px-3 py-3 bg-[#25d366] hover:bg-[#1ebe5b] disabled:bg-zinc-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
              data-testid="compose-whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp
            </button>
          </div>
          <p className="text-xs text-zinc-400 text-center">
            {customerData.phone ? customerData.phone : 'No phone number on file'}
          </p>
        </div>
      </div>
    );
  };

  const renderAlertsTab = () => (
    <div className="space-y-3">
      <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
        <h4 className="font-semibold text-zinc-900 mb-2">Notification Preferences</h4>
        <div className="space-y-3">
          {[
            { label: 'Job Reminders', sublabel: 'SMS & Email', enabled: true },
            { label: 'Invoice Notifications', sublabel: 'Email only', enabled: true },
            { label: 'Payment Confirmations', sublabel: 'SMS & Email', enabled: true },
          ].map((pref, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-zinc-900">{pref.label}</p>
                <p className="text-xs text-zinc-500">{pref.sublabel}</p>
              </div>
              <div className={`w-10 h-6 rounded-full relative ${pref.enabled ? 'bg-emerald-500' : 'bg-zinc-300'}`}>
                <div className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-all ${pref.enabled ? 'right-1' : 'left-1'}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <h4 className="font-semibold text-zinc-700 px-1">Activity Log</h4>
      {alerts.map(alert => (
        <div key={alert.id} className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${
              alert.type === 'reminder' ? 'bg-blue-50' :
              alert.type === 'payment' ? 'bg-emerald-50' :
              'bg-purple-50'
            }`}>
              {alert.type === 'reminder' && <Bell size={16} className="text-emerald-600" />}
              {alert.type === 'payment' && <PoundSterling size={16} className="text-emerald-600" />}
              {alert.type === 'job' && <Check size={16} className="text-purple-600" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-zinc-900">{alert.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{alert.description}</p>
              <p className="text-xs text-zinc-400 mt-1">{formatTimestamp(alert.timestamp)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Use Portal to render at document.body level - ensures modal covers everything including sticky headers
  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[100] flex justify-end" onClick={onClose}>
      <div 
        className="w-full max-w-md bg-zinc-900 h-full overflow-hidden flex flex-col animate-slide-in-right"
        onClick={e => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: swipeOffset === 0 ? 'transform 0.3s ease-out' : 'none',
          opacity: swipeOffset > 0 ? Math.max(0.3, 1 - swipeOffset / 300) : 1,
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        {/* Header with Customer Name */}
        <div className="bg-zinc-900 text-white p-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1"
              data-testid="back-btn"
            >
              <ChevronLeft size={24} />
              <span className="text-sm">Back</span>
            </button>
            
            {/* Customer Name - Center */}
            <h2 className="text-lg font-semibold truncate px-4">
              {customer?.name || job?.customerName || job?.customer_name || customerData?.name || 'Customer'}
            </h2>
            
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="customer-menu-btn"
              >
                <MoreVertical size={20} />
              </button>
              
              {/* Dropdown Menu */}
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-[105]" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-2xl z-[110] overflow-hidden border border-zinc-200">
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowEditForm(true);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                      data-testid="edit-customer-btn"
                    >
                      <Edit3 size={18} className="text-zinc-400" />
                      <span>Edit Customer</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        safeOpenUrl(`/payment-setup/${customer?.id || job?.customerId}`);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                      data-testid="view-payment-page-btn"
                    >
                      <CreditCard size={18} className="text-zinc-400" />
                      <span>View Payment Page</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowMenu(false);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                      data-testid="refresh-invoices-btn"
                    >
                      <RefreshCw size={18} className="text-zinc-400" />
                      <span>Refresh Invoice Statuses</span>
                    </button>
                    
                    <div className="h-px bg-zinc-200 my-1" />
                    
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowDeleteConfirm(true);
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="delete-customer-btn"
                    >
                      <Trash2 size={18} />
                      <span>Delete Customer</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">Delete Customer?</h3>
                <p className="text-zinc-600 mb-6">
                  Are you sure you want to delete <strong>{customer?.name || job?.customerName}</strong>? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setIsDeleting(true);
                      try {
                        const token = localStorage.getItem('streakfree_token');
                        const customerId = customer?.id || job?.customerId;
                        const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          // Notify Work Planner / Visual Planner / Schedule /
                          // Owings / Rounds etc. so they drop this customer's
                          // jobs from view immediately (otherwise they keep
                          // showing the deleted customer until manual refresh).
                          customerChanged(Actions.DELETE, { id: customerId });
                          setShowDeleteConfirm(false);
                          onClose();
                        } else {
                          alert('Failed to delete customer');
                        }
                      } catch (err) {
                        console.error('Delete error:', err);
                        alert('Error deleting customer');
                      } finally {
                        setIsDeleting(false);
                      }
                    }}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    data-testid="confirm-delete-btn"
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Edit Customer Form - Uses NewCustomerForm */}
          {showEditForm && (
            <NewCustomerForm 
              customer={{
                id: customer?.id || job?.customer_id || job?.customerId,
                name: customerData.name,
                email: customerData.email,
                phone: customerData.phone,
                address: customerData.address,
                zone: customerData.zone,
                latitude: customer?.latitude || customer?.geolocation?.lat,
                longitude: customer?.longitude || customer?.geolocation?.lng,
              }}
              onClose={() => {
                setShowEditForm(false);
              }}
              onSave={(updatedCustomer) => {
                setShowEditForm(false);
                // Emit customer change to update ALL dependent areas
                customerChanged(Actions.UPDATE, updatedCustomer);
                // Update local customer data state to reflect changes immediately
                if (updatedCustomer) {
                  // Close and trigger parent refresh
                  onClose();
                }
              }}
            />
          )}
          
          <div className="flex items-center gap-3">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl"
              style={{ backgroundColor: zoneColors[customerData.zone] || '#64748b' }}
            >
              {customerData.name.charAt(0)}
            </div>
            <div>
              <h2 className="text-lg font-bold">{customerData.name}</h2>
              <p className="text-white/70 text-sm">{jobData.services} · £{jobData.price?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-zinc-200">
          <div className="flex">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors ${
                    activeTab === tab.id 
                      ? 'text-emerald-600 border-b-2 border-emerald-600' 
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-zinc-100">
          {activeTab === 'customer' && renderCustomerTab()}
          {activeTab === 'finance' && renderFinanceTab()}
          {activeTab === 'messages' && renderMessagesTab()}
          {activeTab === 'alerts' && renderAlertsTab()}
        </div>
        
        {/* Take Payment Modal */}
        <TakePaymentModal
          isOpen={showTakePayment}
          onClose={() => setShowTakePayment(false)}
          customer={{
            id: customerData.id,
            name: customerData.name,
            balance: localBalance ?? customerData.balance
          }}
          job={customerJobs[selectedJobIndex] ? {
            id: customerJobs[selectedJobIndex].id,
            address: customerJobs[selectedJobIndex].address,
            price: customerJobs[selectedJobIndex].price
          } : null}
          onSuccess={(data) => {
            // Update local balance after successful payment
            if (data?.customer?.new_balance !== undefined) {
              setLocalBalance(data.customer.new_balance);
            }
            // Refresh invoices
            fetchCustomerInvoices();
          }}
        />
        
        {/* SMS Compose Modal */}
        <SMSComposeModal
          isOpen={showSMSCompose}
          onClose={(success) => {
            setShowSMSCompose(false);
            if (success) {
              // Refresh messages after sending
              fetchSmsMessages();
            }
          }}
          recipients={[{
            id: customerData.id,
            customer_id: customerData.id,
            name: customerData.name,
            phone: customerData.phone
          }]}
          context={{
            service: jobData.services || 'service',
            amount: jobData.price,
            balance: customerData.balance,
            date: jobData.nextDue
          }}
        />
        
        {/* WhatsApp Compose Modal */}
        <WhatsAppComposeModal
          isOpen={showWaCompose}
          onClose={(success) => {
            setShowWaCompose(false);
            if (success) {
              fetchSmsMessages();
            }
          }}
          recipient={waRecipient}
          defaultTemplate={(customerData.balance || 0) > 0 ? 'payment_reminder' : 'general_message'}
        />
        
        {/* Invoice Detail Modal */}
        {selectedInvoice && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-end justify-center"
            onClick={() => setSelectedInvoice(null)}
          >
            <div 
              className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden modal-shell-h overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-center py-3">
                <div className="w-10 h-1 bg-zinc-300 rounded-full" />
              </div>
              
              <div className="px-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-zinc-900">{selectedInvoice.invoice_number}</h2>
                    <p className="text-sm text-zinc-500">
                      {new Date(selectedInvoice.created_at).toLocaleDateString('en-GB', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedInvoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                    selectedInvoice.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    selectedInvoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                
                {/* Invoice Details */}
                <div className="bg-zinc-50 rounded-xl p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-zinc-600">Subtotal</span>
                    <span className="font-medium">£{(selectedInvoice.subtotal || selectedInvoice.total)?.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.vat_amount > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-zinc-600">VAT ({selectedInvoice.vat_rate || 20}%)</span>
                      <span className="font-medium">£{selectedInvoice.vat_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-zinc-200">
                    <span className="text-zinc-900 font-semibold">Total</span>
                    <span className="text-lg font-bold text-zinc-900">£{selectedInvoice.total?.toFixed(2)}</span>
                  </div>
                </div>
                
                {/* Actions */}
                {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                  <div className="space-y-2 mb-4">
                    <p className="text-sm font-medium text-zinc-700">Resend Invoice:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={async () => {
                          setSendingInvoice(true);
                          try {
                            const token = localStorage.getItem('streakfree_token');
                            const response = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/send?method=sms`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (response.ok) {
                              toast.success('Invoice sent via SMS');
                            } else {
                              toast.error('Failed to send SMS');
                            }
                          } catch (err) {
                            toast.error('Error sending SMS');
                          }
                          setSendingInvoice(false);
                        }}
                        disabled={sendingInvoice}
                        className="flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:opacity-50"
                      >
                        <MessageSquare size={18} />
                        SMS
                      </button>
                      <button
                        onClick={async () => {
                          setSendingInvoice(true);
                          try {
                            const token = localStorage.getItem('streakfree_token');
                            const response = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/send?method=email`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (response.ok) {
                              toast.success('Invoice sent via Email');
                            } else {
                              toast.error('Failed to send Email');
                            }
                          } catch (err) {
                            toast.error('Error sending Email');
                          }
                          setSendingInvoice(false);
                        }}
                        disabled={sendingInvoice}
                        className="flex items-center justify-center gap-2 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 disabled:opacity-50"
                      >
                        <Mail size={18} />
                        Email
                      </button>
                    </div>
                  </div>
                )}
                
                {/* View PDF */}
                <button
                  onClick={() => window.open(`${API_URL}/api/invoices/${selectedInvoice.id}/pdf`, '_blank')}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-100 text-zinc-700 rounded-xl font-medium hover:bg-zinc-200"
                >
                  <FileText size={18} />
                  View PDF
                </button>
              </div>
              
              <div className="px-6 pb-8">
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="w-full py-3 text-zinc-500 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CustomerDetailModal;
